import { CalendarDate } from '@internationalized/date';
import { maybe } from '@passionware/monads';
import { GenericDateFilter } from 'v5/api/_common/query/filters/GenericDateFilter';
import { NumberFilter } from 'v5/api/_common/query/filters/NumberFilter';
import { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { StringFilter } from 'v5/api/_common/query/filters/StringFilter';

/**
 * Converts a GenericDateFilter to API format.
 * Returns an object with operators like ge, lt, etc.
 * Uses open ranges (ge: day, lt: next_day) for equal and between operations.
 *
 * @param filter - The date filter to convert
 * @param resolver - Optional resolver for preset filters. If not provided, presets will return null.
 * @returns Object with API operators or null if filter cannot be converted
 */
export function convertDateFilterToApi<Preset extends string>(
  filter: GenericDateFilter<Preset>,
  resolver: {
    resolvePreset: (
      preset: Preset
    ) => { from: CalendarDate; to: CalendarDate } | null;
  }
): Record<string, string> | null {
  switch (filter.operator) {
    case 'equal':
      return {
        $gte: filter.value.toString(),
        $lt: filter.value.add({ days: 1 }).toString(),
        type: 'date'
      };
    case 'greaterThan':
      return {
        $gt: filter.value.toString(),
        type: 'date'
      };
    case 'lessThan':
      return {
        $lt: filter.value.toString(),
        type: 'date'
      };
    case 'between':
      return {
        $gte: filter.value.from.toString(),
        $lt: filter.value.to.add({ days: 1 }).toString(),
        type: 'date'
      };
    case 'preset':
      const range = resolver.resolvePreset(filter.value);
      if (range) {
        return {
          $gte: range.from.toString(),
          $lt: range.to.add({ days: 1 }).toString(),
          type: 'date'
        };
      }
      return null;
    default:
      return null;
  }
}

/**
 * Converts a NumberFilter to Objection.js/API format.
 *
 * @param filter - The number filter to convert
 * @returns Object with API operators or null if filter cannot be converted
 */
export function convertNumberFilterToApi(filter: NumberFilter) {
  switch (filter.operator) {
    case 'equal':
      return { $eq: filter.value.toString() };
    case 'notEqual':
      return { $ne: filter.value.toString() };
    case 'greaterThan':
      return { $gt: filter.value.toString() };
    case 'lessThan':
      return { $lt: filter.value.toString() };
    case 'between':
      return {
        $gte: filter.value.from.toString(),
        $lte: filter.value.to.toString()
      };
    default:
      return null;
  }
}

/**
 * Converts a BooleanFilter to Objection.js/API format.
 *
 * @param filter - The boolean filter to convert
 * @returns Object with API operators or null if filter cannot be converted
 */
export function convertBooleanFilterToApi(filter: BooleanFilter) {
  return { $eq: filter.value };
}

/**
 * Converts an EnumFilter to Objection.js/API format for scalar (single-value) fields.
 * matchAny → $in, matchNone → $nin. matchAll is not valid for scalars.
 *
 * @param filter - The enum filter to convert
 * @param valueConverter - Optional function to convert filter values (e.g., string to number)
 * @returns API filter format
 */
export function convertEnumFilterToApi<V>(
  filter: EnumFilter<V>,
  valueConverter?: (value: V) => unknown
): unknown {
  const convertValue = valueConverter ?? ((v: V) => v);
  const convertedValues = filter.value.map(convertValue);

  switch (filter.operator) {
    case 'matchAny':
      return convertedValues.length ? { $in: convertedValues } : null;
    case 'matchNone':
      return convertedValues.length ? { $nin: convertedValues } : null;
    case 'empty':
      return { $empty: true };
    case 'notEmpty':
      return { $empty: false };
    case 'matchAll':
      throw new Error(
        'matchAll is not supported for scalar enum filters; use convertEnumFilterToArrayApi'
      );
  }
}

/**
 * Converts an EnumFilter for array-valued fields (tags, groups, multi-select, etc.).
 * matchAny → bare array (OR), matchAll → $match (AND), matchNone → $nmatch.
 */
export function convertEnumFilterToArrayApi<V>(
  filter: EnumFilter<V>,
  valueConverter?: (value: V) => unknown
) {
  const convertValue = valueConverter ?? ((v: V) => v);
  const convertedValues = filter.value.map(convertValue);

  switch (filter.operator) {
    case 'matchAny':
      return convertedValues.length ? convertedValues : null;
    case 'matchAll':
      return convertedValues.length ? { $match: convertedValues } : null;
    case 'matchNone':
      return convertedValues.length ? { $nmatch: convertedValues } : null;
    case 'empty':
      return { $empty: true };
    case 'notEmpty':
      return { $empty: false };
  }
}

/**
 * Creates a converter function for EnumFilter.
 * Useful for building filter converters with buildFilterConverter.
 *
 * @param valueConverter - Optional function to convert filter values (e.g., string to number)
 * @returns A converter function that takes an EnumFilter and returns API format
 */
export function createEnumFilterConverter<V>(
  valueConverter?: (value: V) => unknown
) {
  return (filter: EnumFilter<V> | null | undefined): unknown | null => {
    if (!maybe.isPresent(filter)) {
      return null;
    }
    return convertEnumFilterToApi(filter, valueConverter);
  };
}

export function createEnumArrayFilterConverter<V>(
  valueConverter?: (value: V) => unknown
) {
  return (filter: EnumFilter<V> | null | undefined) => {
    if (!maybe.isPresent(filter)) {
      return null;
    }
    return convertEnumFilterToArrayApi(filter, valueConverter);
  };
}

const escapeLikePattern = (value: string) =>
  value.replace(/[%_]/g, char => `\\${char}`);

export function convertStringFilterToApi(
  filter: StringFilter
): Record<string, string> | null {
  switch (filter.operator) {
    case 'equals':
      return { $eq: filter.value };
    case 'contains':
      return { $like: `%${escapeLikePattern(filter.value)}%` };
    case 'notContains':
      return { $nlike: `%${escapeLikePattern(filter.value)}%` };
    default:
      return null;
  }
}

export function createStringFilterConverter() {
  return (
    filter: StringFilter | null | undefined
  ): Record<string, string> | null => {
    if (!maybe.isPresent(filter)) {
      return null;
    }
    return convertStringFilterToApi(filter);
  };
}

/**
 * Creates a converter function for GenericDateFilter.
 * Useful for building filter converters with buildFilterConverter.
 *
 * @param resolver - Resolver for preset filters
 * @returns A converter function that takes a GenericDateFilter and returns API format
 */
export function createDateFilterConverter<Preset extends string>(resolver: {
  resolvePreset: (
    preset: Preset
  ) => { from: CalendarDate; to: CalendarDate } | null;
}) {
  return (
    filter: GenericDateFilter<Preset> | null | undefined
  ): Record<string, string> | null => {
    if (!maybe.isPresent(filter)) {
      return null;
    }
    return convertDateFilterToApi(filter, resolver);
  };
}

/**
 * Options for timestamp date filter conversion
 */
export type TimestampDateFilterOptions<Preset extends string> = {
  /**
   * The API field name for the date filter
   */
  fieldName: string;
  /**
   * Whether to use open ranges (gte/lt with next day) for timestamp fields.
   * When true: "equal" uses gte: day, lt: next_day
   * When false: "equal" uses gte: day, lte: day
   * Default: true (recommended for timestamp fields)
   */
  useOpenRanges?: boolean;
  /**
   * Optional dataType hint for the API (e.g., 'date')
   */
  dataType?: string;
  /**
   * Resolver for preset filters
   */
  resolvePreset?: (
    preset: Preset
  ) => { from: CalendarDate; to: CalendarDate } | null;
  /**
   * Convert CalendarDate to API value. Defaults to ISO string.
   */
  dateToValue?: (date: CalendarDate) => string | number;
};

/**
 * Creates a converter for date filters targeting a single timestamp field.
 * Uses open ranges by default (gte: day 00:00, lt: next_day 00:00) which is
 * correct for timestamp fields where you want to include the entire day.
 *
 * @example
 * ```typescript
 * const convertCallsheetDate = createTimestampDateFilterConverter({
 *   fieldName: 'callsheet_date',
 *   dataType: 'date',
 *   resolvePreset: (preset) => resolvePresetRange(preset)
 * });
 * ```
 */
export function createTimestampDateFilterConverter<Preset extends string>(
  options: TimestampDateFilterOptions<Preset>
) {
  const {
    fieldName,
    useOpenRanges = true,
    dataType,
    resolvePreset,
    dateToValue = (date: CalendarDate) => date.toString()
  } = options;

  return (
    filter: GenericDateFilter<Preset> | null | undefined
  ): Record<string, unknown> | null => {
    if (!maybe.isPresent(filter)) {
      return null;
    }

    const result: Record<string, unknown> = {};
    const filterObj: Record<string, string | number> = {};

    if (dataType) {
      filterObj.dataType = dataType;
    }

    const toNextDay = (date: CalendarDate) => date.add({ days: 1 });

    switch (filter.operator) {
      case 'equal': {
        if (useOpenRanges) {
          // Open range: gte day 00:00, lt next_day 00:00
          filterObj.$gte = dateToValue(filter.value);
          filterObj.$lt = dateToValue(toNextDay(filter.value));
        } else {
          // Closed range: gte day, lte day
          filterObj.$gte = dateToValue(filter.value);
          filterObj.$lte = dateToValue(filter.value);
        }
        break;
      }
      case 'greaterThan': {
        filterObj.$gte = dateToValue(filter.value);
        break;
      }
      case 'lessThan': {
        filterObj.$lt = dateToValue(filter.value);
        break;
      }
      case 'between': {
        if (filter.value.from) {
          filterObj.$gte = dateToValue(filter.value.from);
        }
        if (filter.value.to) {
          if (useOpenRanges) {
            filterObj.$lt = dateToValue(toNextDay(filter.value.to));
          } else {
            filterObj.$lte = dateToValue(filter.value.to);
          }
        }
        break;
      }
      case 'preset': {
        if (resolvePreset) {
          const range = resolvePreset(filter.value);
          if (range) {
            filterObj.$gte = dateToValue(range.from);
            if (useOpenRanges) {
              filterObj.$lt = dateToValue(toNextDay(range.to));
            } else {
              filterObj.$lte = dateToValue(range.to);
            }
          }
        }
        break;
      }
    }

    // Only return if we have actual filter constraints (not just dataType)
    const hasConstraints = Object.keys(filterObj).some(k => k !== 'dataType');
    if (hasConstraints) {
      result[fieldName] = filterObj;
      return result;
    }

    return null;
  };
}

/**
 * Options for date range filter conversion (two fields: start and end)
 */
export type DateRangeFilterOptions<Preset extends string> = {
  /**
   * The API field name for the start date
   */
  startFieldName: string;
  /**
   * The API field name for the end date
   */
  endFieldName: string;
  /**
   * Resolver for preset filters
   */
  resolvePreset?: (
    preset: Preset
  ) => { from: CalendarDate; to: CalendarDate } | null;
  /**
   * Convert CalendarDate to API value. Defaults to unix timestamp.
   */
  dateToValue?: (date: CalendarDate) => string | number;
};

/**
 * Creates a converter for date filters targeting a date range (two fields).
 * Used for entities with start_date and end_date fields where you want to
 * find entities that overlap with the filter range.
 *
 * @example
 * ```typescript
 * const convertProductionDate = createDateRangeFilterConverter({
 *   startFieldName: 'production_start_date',
 *   endFieldName: 'production_end_date',
 *   resolvePreset: (preset) => resolvePresetRange(preset),
 *   dateToValue: calendarDateToTimestamp
 * });
 * ```
 */
export function createDateRangeFilterConverter<Preset extends string>(
  options: DateRangeFilterOptions<Preset>
) {
  const {
    startFieldName,
    endFieldName,
    resolvePreset,
    dateToValue = (date: CalendarDate) => {
      const jsDate = date.toDate('UTC');
      return Math.floor(jsDate.getTime() / 1000);
    }
  } = options;

  return (
    filter: GenericDateFilter<Preset> | null | undefined
  ): Record<string, unknown> | null => {
    if (!maybe.isPresent(filter)) {
      return null;
    }

    const result: Record<string, unknown> = {};

    const setConstraint = (
      field: string,
      operator: '$gte' | '$lte' | '$gt' | '$lt',
      value: CalendarDate
    ) => {
      const existing = (result[field] as Record<string, unknown>) ?? {};
      result[field] = {
        ...existing,
        [operator]: dateToValue(value)
      };
    };

    switch (filter.operator) {
      case 'equal': {
        // For entities that overlap with this date:
        // start_date <= date (entity starts on or before this date)
        // end_date >= date (entity ends on or after this date)
        setConstraint(startFieldName, '$lte', filter.value);
        setConstraint(endFieldName, '$gte', filter.value);
        break;
      }
      case 'greaterThan': {
        // Entity must end after this date
        setConstraint(endFieldName, '$gte', filter.value);
        break;
      }
      case 'lessThan': {
        // Entity must start strictly before this date
        setConstraint(startFieldName, '$lt', filter.value);
        break;
      }
      case 'between': {
        // Entity overlaps with range if:
        // end_date >= from (entity extends into or past the start of range)
        // start_date <= to (entity begins at or before the end of range)
        if (filter.value.from) {
          setConstraint(endFieldName, '$gte', filter.value.from);
        }
        if (filter.value.to) {
          setConstraint(startFieldName, '$lte', filter.value.to);
        }
        break;
      }
      case 'preset': {
        if (resolvePreset) {
          const range = resolvePreset(filter.value);
          if (range) {
            setConstraint(endFieldName, '$gte', range.from);
            setConstraint(startFieldName, '$lte', range.to);
          }
        }
        break;
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  };
}

/**
 * Type for a filter converter function that converts a single filter to API format.
 */
export type FilterConverter<T> = (
  filter: T | null | undefined
) => unknown | null;

/**
 * Builds a strongly-typed converter function for an entire filters object.
 *
 * @param converters - Object mapping filter field names to their converter functions
 * @param fieldMapping - Optional mapping from filter field names to API field names.
 *                       If not provided, filter field names are used as-is.
 * @returns A function that converts the entire filters object to API format
 *
 * @example
 * ```typescript
 * const convertFilters = buildFilterConverter({
 *   status: createEnumFilterConverter<string>(id => Number(id)),
 *   productionDate: createDateFilterConverter<'today' | 'lastWeek' | 'lastMonth'>()
 * }, {
 *   status: 'status_id',
 *   productionDate: 'production_dates'
 * });
 *
 * const apiFilters = convertFilters({
 *   status: { operator: 'matchAny', value: ['1', '2'] },
 *   productionDate: { operator: 'equal', value: today() }
 * });
 * // Result: { status_id: [1, 2], production_dates: { $eq: '2024-01-01' } }
 * ```
 */
export function buildFilterConverter<
  TFilters extends Record<string, any>,
  TFieldMapping extends Partial<Record<keyof TFilters, string>> = {}
>(
  converters: {
    [K in keyof TFilters]: FilterConverter<TFilters[K]>;
  },
  fieldMapping?: TFieldMapping
): (
  filters:
    | {
        [K in keyof TFilters]: TFilters[K] | null | undefined;
      }
    | null
    | undefined
) => Record<string, unknown> {
  return (
    filters:
      | {
          [K in keyof TFilters]: TFilters[K] | null | undefined;
        }
      | null
      | undefined
  ): Record<string, unknown> => {
    const apiFilters: Record<string, unknown> = {};

    if (!maybe.isPresent(filters)) {
      return apiFilters;
    }

    for (const [fieldName, converter] of Object.entries(converters) as Array<
      [keyof TFilters, FilterConverter<any>]
    >) {
      const filterValue = filters[fieldName];
      const converted = converter(filterValue);

      if (converted !== null && converted !== undefined) {
        // Use field mapping if provided, otherwise use the original field name
        const apiFieldName = fieldMapping?.[fieldName] ?? (fieldName as string);
        apiFilters[apiFieldName] = converted;
      }
    }

    return apiFilters;
  };
}

export const createSingleFieldDateRangeFilterConverter =
  <Preset extends string>(resolver: {
    resolvePreset: (
      preset: Preset
    ) => { from: CalendarDate; to: CalendarDate } | null;
  }) =>
  (filter: GenericDateFilter<Preset>) => {
    switch (filter.operator) {
      case 'equal':
        return {
          $eq: filter.value.toString(),
          type: 'date_range'
        };
      case 'greaterThan':
        return {
          $gte: filter.value.toString(),
          type: 'date_range'
        };
      case 'lessThan':
        return {
          $lte: filter.value.toString(),
          type: 'date_range'
        };
      case 'between':
        return {
          $between: [filter.value.from.toString(), filter.value.to.toString()],
          type: 'date_range'
        };
      case 'preset':
        const range = resolver.resolvePreset(filter.value);
        if (range) {
          return {
            $between: [
              range.from.toString(),
              range.to.add({ days: 1 }).toString()
            ],
            type: 'date_range'
          };
        }
        return null;
      default:
        return null;
    }
  };
